import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { getSubscriptionsAdmin } from "../../../api/arbitrage.api";
import Pagination from "../../Pagination/Pagination";

const statusStyle = {
  active: "bg-green-100 text-green-700",
  completed: "bg-gray-100 text-gray-500",
  cancelled: "bg-red-100 text-red-500",
};

const ITEMS_PER_PAGE = 25;

const ArbitrageSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const res = await getSubscriptionsAdmin();
      setSubscriptions(res.data);
    } catch {
      toast.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  // Filter by status + search
  const filtered = subscriptions.filter((s) => {
    const matchStatus = filter === "all" || s.status === filter;
    const matchSearch =
      !search ||
      s.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.user_uuid?.toLowerCase().includes(search.toLowerCase()) ||
      s.package_name?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  // Reset page when filter/search changes
  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-gray-800">
            Arbitrage Subscriptions
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-0.5">
            {filtered.length} total subscriptions
          </p>
        </div>
        <button
          onClick={fetchSubscriptions}
          className="w-full md:w-auto px-4 py-2 border border-gray-200 text-sm rounded-lg hover:bg-gray-50 text-gray-600 transition"
        >
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {["active", "completed", "cancelled"].map((s) => (
          <div key={s} className="bg-gray-50 rounded-xl p-3 md:p-4">
            <p className="text-xs text-gray-400 capitalize mb-1">{s}</p>
            <p className="text-xl md:text-2xl font-bold text-gray-800">
              {subscriptions.filter((x) => x.status === s).length}
            </p>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            width="14"
            height="14"
            fill="none"
            viewBox="0 0 16 16"
          >
            <circle
              cx="7"
              cy="7"
              r="5"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M11 11l3 3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by user or package..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-xl shadow-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "active", "completed", "cancelled"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 text-xs rounded-lg capitalize font-medium transition ${
                filter === f
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">
          No subscriptions found
        </div>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="block space-y-3 md:hidden">
            {paginated.map((sub) => (
              <div
                key={sub.id}
                className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {sub.user_uuid}
                    </p>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle[sub.status]}`}
                  >
                    {sub.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div>
                    <p className="text-gray-400">Package</p>
                    <p className="text-gray-700 font-medium">
                      {sub.package_name}
                      <span className="text-gray-400 ml-1">
                        ({sub.duration_days}d)
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Coin</p>
                    <p className="text-gray-700 font-medium">{sub.coin_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Amount</p>
                    <p className="text-gray-800 font-semibold">
                      {Number(sub.amount).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Daily Rate</p>
                    <p className="font-semibold" style={{ color: "#7c3aed" }}>
                      {sub.daily_rate}%
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Earned</p>
                    <p className="text-green-600 font-semibold">
                      +{Number(sub.total_earned).toFixed(4)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">End Date</p>
                    <p className="text-gray-600">
                      {format(new Date(sub.end_date), "dd MMM yyyy")}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {[
                    "User",
                    "Package",
                    "Coin",
                    "Amount",
                    "Rate",
                    "Earned",
                    "End Date",
                    "Status",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((sub) => (
                  <tr
                    key={sub.id}
                    className="border-b border-gray-50 hover:bg-gray-50 transition"
                  >
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-800">
                        {sub.user_name || "—"}
                      </p>
                      <p className="text-xs text-gray-400">{sub.user_uuid}</p>
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {sub.package_name}
                      <span className="ml-1 text-xs text-gray-400">
                        ({sub.duration_days}d)
                      </span>
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-700">
                      {sub.coin_id}
                    </td>
                    <td className="px-5 py-3 text-gray-800 font-medium">
                      {Number(sub.amount).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-indigo-600 font-medium">
                      {sub.daily_rate}%
                    </td>
                    <td className="px-5 py-3 text-green-600 font-medium">
                      +{Number(sub.total_earned).toFixed(4)}
                    </td>
                    <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {format(new Date(sub.end_date), "dd MMM yyyy")}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle[sub.status]}`}
                      >
                        {sub.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <Pagination page={page} totalPages={totalPages} setPage={setPage} />
          </div>
        </>
      )}
    </div>
  );
};

export default ArbitrageSubscriptions;
